import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { useAuthStore, type AuthUser } from '../../store/authStore';
import { Loader2 } from '../../icons';
import { getSharedLang, type Lang } from '../../lib/base-i18n';
import './MfaVerifyPage.css';

// Cycle 49 — i18n rollout 잔여 마감 (cycle 46 학습 phase 5 fallback chain 적용).
// lang → en → ko fallback. MfaVerifyPage 의 사용자 가시 string 만 — error code 별
// 분기 메시지도 동일 dict.
const MFA_MESSAGES: Record<Lang, {
  heading: string;
  subheading_totp: string;
  subheading_backup: string;
  method_totp: string;
  method_backup: string;
  label_totp: string;
  label_backup: string;
  submit_verifying: string;
  submit_confirm: string;
  hint_time_left: string;
  link_other_login: string;
  err_token_expired: string;
  err_mfa_invalid: string;
  err_generic: string;
}> = {
  ko: {
    heading: '2단계 인증',
    subheading_totp: '인증 앱의 6자리 코드를 입력하세요',
    subheading_backup: '백업 코드를 입력하세요',
    method_totp: 'TOTP',
    method_backup: '백업 코드',
    label_totp: '6자리 코드',
    label_backup: '백업 코드 (10자리 영숫자)',
    submit_verifying: '검증 중...',
    submit_confirm: '확인',
    hint_time_left: '남은 시간',
    link_other_login: '다른 계정으로 로그인',
    err_token_expired: '인증 시간 만료 — 다시 로그인하세요',
    err_mfa_invalid: '코드가 올바르지 않습니다',
    err_generic: '검증 실패 — 잠시 후 다시 시도하세요',
  },
  en: {
    heading: 'Two-factor authentication',
    subheading_totp: 'Enter the 6-digit code from your authenticator app',
    subheading_backup: 'Enter a backup code',
    method_totp: 'TOTP',
    method_backup: 'Backup code',
    label_totp: '6-digit code',
    label_backup: 'Backup code (8-16 alphanumeric)',
    submit_verifying: 'Verifying…',
    submit_confirm: 'Submit',
    hint_time_left: 'Time left',
    link_other_login: 'Sign in with a different account',
    err_token_expired: 'Verification expired — please sign in again',
    err_mfa_invalid: 'The code is incorrect',
    err_generic: 'Verification failed — please try again shortly',
  },
  ja: {
    heading: '2要素認証',
    subheading_totp: '認証アプリの6桁コードを入力してください',
    subheading_backup: 'バックアップコードを入力してください',
    method_totp: 'TOTP',
    method_backup: 'バックアップコード',
    label_totp: '6桁コード',
    label_backup: 'バックアップコード(8-16英数字)',
    submit_verifying: '検証中...',
    submit_confirm: '確認',
    hint_time_left: '残り時間',
    link_other_login: '別アカウントでログイン',
    err_token_expired: '認証時間切れ — 再ログインしてください',
    err_mfa_invalid: 'コードが正しくありません',
    err_generic: '検証失敗 — しばらくしてから再試行してください',
  },
};

function tMfa(): typeof MFA_MESSAGES['ko'] {
  // cycle 46 학습 phase 5 fallback chain: lang → en → ko
  return MFA_MESSAGES[getSharedLang()] ?? MFA_MESSAGES.en;
}

/**
 * MfaVerifyPage (P1-20). LoginPage 가 mfa_required 응답 받으면 navigate state 로
 * temp_token + 옵션을 전달. 본 페이지가 TOTP 6자리 또는 백업 코드 입력 → POST
 * /auth/mfa/verify → setSession.
 *
 * temp_token TTL 5분 — 만료 시 /login 으로 회귀.
 *
 * 본 페이지가 navigate state 없이 직접 접근되면 /login 으로 redirect (방어).
 */

interface NavState {
  tempToken?: string;
  tempTokenExpiresAt?: number;
  mfaEnrollRequired?: boolean;
  mfaMethods?: Array<'totp' | 'backup_code'>;
  redirectTo?: string;
}

// auth v1.5 cookie cutover — verify 응답은 Set-Cookie 로 토큰 발급. body 토큰은 frontend 가
// 사용 X (B1.17 단계에서 backend body 도 제거 예정). 현재는 정의 자체 불필요.

interface UserMe {
  user: {
    id: string; email: string; name: string;
    role: AuthUser['role']; institutionId: string;
    // MFA 로그인 직후 사이드바 아바타 즉시 표시 — /users/me 가 raw avatarUrl 동봉.
    avatarUrl?: string | null;
  };
}

interface ApiError { code?: string; message?: string }

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const state = (location.state as NavState | null) ?? {};
  const tempToken = state.tempToken;
  const expiresAt = state.tempTokenExpiresAt;
  const redirectTo = state.redirectTo ?? '/dashboard';

  const [method, setMethod] = useState<'totp' | 'backup_code'>('totp');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const t = tMfa();

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        setErrorMessage(t.err_token_expired);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, t.err_token_expired]);

  // navigate state 미존재 — 직접 진입 차단.
  if (!tempToken) {
    return <Navigate to="/login" replace />;
  }

  /**
   * 2차 인증 미등록 → **등록 화면으로 보낸다** (2026-08-25).
   * 그전에는 `/login` 으로 되돌려 보내기만 했는데, 로그인 화면이 그 상태를 읽지 않아
   * **아무 말 없이 튕기는 무한 고리**가 됐다 — 새로 관리자가 된 사람이 못 들어왔다.
   * 토큰은 그대로 넘긴다(등록도 mfa-pending 토큰으로 한다).
   */
  if (state.mfaEnrollRequired) {
    return (
      <Navigate
        to="/auth/mfa-enroll"
        replace
        state={{ tempToken, tempTokenExpiresAt: expiresAt, redirectTo }}
      />
    );
  }

  const codeNormalized = code.trim().toUpperCase();
  const isValid = method === 'totp'
    ? /^\d{6}$/.test(code.trim())
    : /^[A-Z0-9]{8,16}$/.test(codeNormalized);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!isValid || submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      // bare axios — apiClient 의 401 인터셉터 우회 (mfa-pending 토큰은 아직 full session 아님).
      // 응답이 Set-Cookie wc_at/wc_rt 발급 (auth v1.5) — withCredentials:true 로 cookie jar 저장.
      await axios.post(
        `${baseURL}/auth/mfa/verify`,
        { code: method === 'totp' ? code.trim() : codeNormalized, method },
        {
          headers: { Authorization: `Bearer ${tempToken}` },
          withCredentials: true,
          timeout: 30_000,
        },
      );
      // /users/me 는 cookie 자동 전송 — Authorization 헤더 불필요.
      const me = await axios.get<UserMe>(`${baseURL}/users/me`, {
        withCredentials: true,
        timeout: 30_000,
      });
      setSession({
        id: Number(me.data.user.id),
        email: me.data.user.email,
        name: me.data.user.name,
        role: me.data.user.role,
        institutionId: Number(me.data.user.institutionId),
        avatarUrl: me.data.user.avatarUrl ?? null,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      const code = ax.response?.data?.code;
      const message = ax.response?.data?.message;
      setErrorMessage(
        message ??
          (code === 'MFA_INVALID' ? t.err_mfa_invalid :
           code === 'TOKEN_EXPIRED' ? t.err_token_expired :
           t.err_generic),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mfa-page">
      <div className="mfa-card">
        <h2 className="mfa-card__heading">{t.heading}</h2>
        <p className="mfa-card__subheading">
          {method === 'totp' ? t.subheading_totp : t.subheading_backup}
        </p>

        {errorMessage && <div role="alert" className="mfa-card__error">{errorMessage}</div>}

        {state.mfaMethods && state.mfaMethods.includes('backup_code') && (
          <div role="tablist" className="mfa-card__method-toggle">
            <button
              type="button"
              role="tab"
              aria-selected={method === 'totp'}
              className={`mfa-card__method-button ${method === 'totp' ? 'mfa-card__method-button--active' : ''}`}
              onClick={() => { setMethod('totp'); setCode(''); }}
            >
              {t.method_totp}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={method === 'backup_code'}
              className={`mfa-card__method-button ${method === 'backup_code' ? 'mfa-card__method-button--active' : ''}`}
              onClick={() => { setMethod('backup_code'); setCode(''); }}
            >
              {t.method_backup}
            </button>
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="mfa-card__field">
            <label htmlFor="mfa-code" className="mfa-card__label">
              {method === 'totp' ? t.label_totp : t.label_backup}
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode={method === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              className={`mfa-card__input ${method === 'backup_code' ? 'mfa-card__input--backup' : ''}`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={method === 'totp' ? 6 : 16}
              autoFocus
              required
            />
          </div>

          <button
            type="submit"
            className="mfa-card__button"
            disabled={!isValid || submitting || secondsLeft === 0}
            aria-busy={submitting}
          >
            {submitting ? (
              <><Loader2 size={16} className="mfa-card__spinner" /> {t.submit_verifying}</>
            ) : (
              t.submit_confirm
            )}
          </button>
        </form>

        <div className="mfa-card__hint">
          {secondsLeft != null && secondsLeft > 0 && (
            <p>{t.hint_time_left}: {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}</p>
          )}
          <p><Link to="/login">{t.link_other_login}</Link></p>
        </div>
      </div>
    </div>
  );
}
